/* Ports of flutter_flow_util.dart helpers (only what the app uses).
 * All user-visible strings come from the i18n dictionary. */

import { tr } from '@/i18n'

/** FlutterFlow's valueOrDefault: null OR EMPTY STRING falls back.
 * `valueOrDefault(userName, 'User')` — record getters coalesce a missing
 * field to '', so the empty check is what actually fires. */
export function valueOrDefault(
  value: string | null | undefined,
  defaultValue: string,
): string {
  return value == null || value === '' ? defaultValue : value
}

/* dateTimeFormat("relative", d) -> timeago.format(d) — a literal port of the
 * Dart timeago package's algorithm + EnglishMessages. date-fns is NOT used:
 * its formatDistanceToNow produces different strings ("less than a minute
 * ago" vs "a moment ago"). Threshold table (timeago.dart):
 *   s<45 'a moment' | s<90 'a minute' | m<45 '$m minutes'
 *   m<90 'about an hour' | h<24 '$h hours' | h<48 'a day'
 *   d<30 '$d days' | d<60 'about a month' | d<365 '$mo months'
 *   y<2 'about a year' | else '$y years'   — suffix ' ago'. */
export function timeagoEn(date: Date, now: Date = new Date()): string {
  const elapsed = now.getTime() - date.getTime()
  const seconds = elapsed / 1000
  const minutes = seconds / 60
  const hours = minutes / 60
  const days = hours / 24
  const months = days / 30
  const years = days / 365

  const T = tr.timeago
  let result: string
  if (seconds < 45) result = T.aMoment
  else if (seconds < 90) result = T.aMinute
  else if (minutes < 45) result = T.minutes(Math.round(minutes))
  else if (minutes < 90) result = T.aboutAnHour
  else if (hours < 24) result = T.hours(Math.round(hours))
  else if (hours < 48) result = T.aDay
  else if (days < 30) result = T.days(Math.round(days))
  else if (days < 60) result = T.aboutAMonth
  else if (days < 365) result = T.months(Math.round(months))
  else if (years < 2) result = T.aboutAYear
  else result = T.years(Math.round(years))

  return T.suffix(result)
}

/** dateTimeFormat("H:mm MM/dd/yy") — intl semantics: H unpadded 0-23, the
 * rest zero-padded, 2-digit year. E.g. '20:27 11/27/25', '9:05 01/03/26'. */
export function formatClock(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getHours()}:${p(d.getMinutes())} ${p(d.getMonth() + 1)}/${p(d.getDate())}/${String(d.getFullYear() % 100).padStart(2, '0')}`
}

/** convertDistanceToText (custom_functions.dart): null -> "- m";
 * < 1000 -> "N m"; else 1-decimal km ("1.2 km"). */
export function convertDistanceToText(distance: number | null | undefined): string {
  if (distance == null) return tr.distance.unknown
  if (distance < 1000) return tr.distance.meters(distance)
  return tr.distance.km((distance / 1000).toFixed(1))
}

/** dateTimeFormat dispatcher for the two formats the app uses; null-safe
 * like the Dart original (null date -> ''). */
export function dateTimeFormat(
  format: 'relative' | 'H:mm MM/dd/yy',
  date: Date | null | undefined,
): string {
  if (date == null) return ''
  return format === 'relative' ? timeagoEn(date) : formatClock(date)
}
